import React, { useEffect } from 'react';
import { UseSensorTypesReturn } from '../hooks/useSensorTypes';
import { CalibrationInputFieldConfig } from '../../sensors/types';
import { measurementUnitFromRangeValue, pressureUnitInputHints } from '../../utils/rangeUnit';
import { isDisplacementCategory } from '../../utils/displacementModelFamily';

interface SensorInfoStepProps {
  individualCalibrationData: any;
  setIndividualCalibrationData: React.Dispatch<React.SetStateAction<any>>;
  sensorTypes: UseSensorTypesReturn;
  calibrationFieldConfig?: CalibrationInputFieldConfig | null;
  onSensorTypeChange?: (sensorType: string) => void;
  onCategoryChange?: (category: string) => void;
  onModelChange?: (model: string) => void;
}

export const SensorInfoStep: React.FC<SensorInfoStepProps> = ({
  individualCalibrationData,
  setIndividualCalibrationData,
  sensorTypes,
  calibrationFieldConfig,
  onSensorTypeChange,
  onCategoryChange,
  onModelChange,
}) => {
  const isDisplacement = isDisplacementCategory(individualCalibrationData.category);
  // When every matching sensor_type row already has meta_data configured,
  // the family is known up front (availableModelFamilies resolves to exactly
  // one option) and there's nothing for the user to pick.
  const showModelFamilyDropdown = isDisplacement && sensorTypes.familySelectionRequired;
  const resolvedModelFamily =
    isDisplacement && !sensorTypes.familySelectionRequired
      ? sensorTypes.availableModelFamilies[0]
      : null;

  // Auto-sync modelFamily from the configured family once it's known, so
  // downstream model/range filtering works without a user selection.
  useEffect(() => {
    if (
      resolvedModelFamily &&
      individualCalibrationData.modelFamily !== resolvedModelFamily.value
    ) {
      setIndividualCalibrationData((prev: any) => ({
        ...prev,
        modelFamily: resolvedModelFamily.value,
        model: '',
        range: '',
      }));
      sensorTypes.updateModels(
        individualCalibrationData.sensorType,
        individualCalibrationData.category,
        resolvedModelFamily.value
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedModelFamily?.value]);

  const handleSensorTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sensorType = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      sensorType,
      category: '',
      modelFamily: '',
      model: '',
      range: '',
    }));
    sensorTypes.updateCategories(sensorType);
    onSensorTypeChange?.(sensorType);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      category,
      modelFamily: '',
      model: '',
      range: '',
    }));
    if (isDisplacementCategory(category)) {
      sensorTypes.updateModelFamilies(individualCalibrationData.sensorType, category);
    } else {
      sensorTypes.updateModels(individualCalibrationData.sensorType, category);
    }
    onCategoryChange?.(category);
  };

  const handleModelFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const modelFamily = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      modelFamily,
      model: '',
      range: '',
    }));
    sensorTypes.updateModels(
      individualCalibrationData.sensorType,
      individualCalibrationData.category,
      modelFamily
    );
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const model = e.target.value;
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      model,
      range: '',
    }));
    sensorTypes.updateRanges(
      individualCalibrationData.sensorType,
      individualCalibrationData.category,
      model
    );
    onModelChange?.(model);
  };

  const range = individualCalibrationData.range ?? '';
  const unitFromRange = measurementUnitFromRangeValue(range);

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Sensor Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Serial Number <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            value={individualCalibrationData.sensorId ?? ''}
            onChange={(e) =>
              setIndividualCalibrationData((prev: any) => ({
                ...prev,
                sensorId: e.target.value,
              }))
            }
            className="input w-full"
            placeholder="Enter sensor serial number (e.g., 5756750)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sensor Type <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={individualCalibrationData.sensorType ?? ''}
            onChange={handleSensorTypeChange}
            className="input w-full"
            disabled={sensorTypes.isLoadingSensorTypes}
          >
            <option value="">
              {sensorTypes.isLoadingSensorTypes
                ? 'Loading...'
                : 'Select sensor type'}
            </option>
            {sensorTypes.availableSensorTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={individualCalibrationData.category ?? ''}
            onChange={handleCategoryChange}
            className="input w-full"
            disabled={!individualCalibrationData.sensorType}
          >
            <option value="">Select category</option>
            {sensorTypes.availableCategories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        {showModelFamilyDropdown && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Displacement Sensor Type <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              value={individualCalibrationData.modelFamily ?? ''}
              onChange={handleModelFamilyChange}
              className="input w-full"
              disabled={
                !individualCalibrationData.sensorType ||
                !individualCalibrationData.category
              }
            >
              <option value="">Select sensor type (e.g. EDE-VXX-RC)</option>
              {sensorTypes.availableModelFamilies.map((family) => (
                <option key={family.value} value={family.value}>
                  {family.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {isDisplacement && resolvedModelFamily && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Displacement Sensor Type
            </label>
            <div className="input w-full bg-gray-100 flex items-center">
              {resolvedModelFamily.label}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={individualCalibrationData.model ?? ''}
            onChange={handleModelChange}
            className="input w-full"
            disabled={
              !individualCalibrationData.sensorType ||
              !individualCalibrationData.category ||
              (isDisplacement && !individualCalibrationData.modelFamily)
            }
          >
            <option value="">Select model</option>
            {sensorTypes.availableModels.map((model) => (
              <option key={model.value} value={model.value}>
                {model.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Range <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={range}
            onChange={(e) =>
              setIndividualCalibrationData((prev: any) => ({
                ...prev,
                range: e.target.value,
              }))
            }
            className="input w-full"
            disabled={
              !individualCalibrationData.sensorType ||
              !individualCalibrationData.category ||
              !individualCalibrationData.model
            }
          >
            <option value="">Select range</option>
            {sensorTypes.availableRanges.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {calibrationFieldConfig?.additionalFields &&
          calibrationFieldConfig.additionalFields.length > 0 &&
          calibrationFieldConfig.additionalFields
            .filter((field) => {
              if (field.condition) {
                return field.condition(individualCalibrationData);
              }
              return true;
            })
            .map((field: any) => {
              const tied = field.disableUntilRangeSelected === true;
              const hints = tied
                ? pressureUnitInputHints(unitFromRange, field.unit ?? 'MPa')
                : null;
              const unitForLabel = hints ? hints.unitLabel : field.unit;
              const placeholder = hints?.placeholder ?? field.placeholder ?? '';
              const step = hints?.step ?? field.step ?? '0.01';
              const disabled =
                !!tied && !range.trim();

              return (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.label}
                    {field.required && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                    {unitForLabel ? (
                      <span className="text-gray-500 ml-1">
                        ({unitForLabel})
                      </span>
                    ) : null}
                  </label>
                  <input
                    type={field.type ?? 'text'}
                    value={(individualCalibrationData as any)[field.key] ?? ''}
                    onChange={(e) =>
                      setIndividualCalibrationData((prev: any) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    className="input w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder={
                      disabled
                        ? 'Select range first'
                        : placeholder
                    }
                    step={step}
                    disabled={disabled}
                  />
                </div>
              );
            })}
      </div>
    </div>
  );
};
